import dayjs from "dayjs";
import {
  ShipmentDetails,
  type ShipmentDocument,
  type ShipmentHistory,
  type ShipmentNotes,
  ShipmentInsights,
} from "../../hooks/react-query/dispatch/useShipments";
import { Shipment } from "../../hooks/react-query/dispatch/useStops";
import { getBusinessDayStart } from "../mockUtils";
import { tagNameToShipmentTagMap } from "../../types/shipmentTag.type";
import { mockDispatchMiscellaneous, mockDispatchStops } from "./stops.mock";

const mockAddedAccessorials = [
  {
    id: 74,
    stopId: 1,
    name: "RESI",
    description: "Residential",
    colorHex: "#6B8DEB",
    position: 60,
  },
  {
    id: 77,
    stopId: 1,
    name: "INDL",
    description: "Inside Delivery",
    colorHex: "#9CA8E5",
    position: 90,
  },
  {
    id: 6,
    stopId: 2,
    name: "LG",
    description: "Liftgate",
    colorHex: "#679CB2",
    position: 40,
  },
];

const defaultData = {
  attemptLocation: {
    latitude: 34.1432,
    longitude: -117.2447,
  },
  receivedBy: {
    name: "John Doe",
    email: "john_doe@gmail.com",
    signatureUrl: "/empty-truck.png",
    createdOn: getBusinessDayStart().add(1, "hour"),
  },
  shipper: {
    name: "Landmark Ceramic",
    address: "1427 N Main ST TN 38647",
    city: "Mount Pleasant",
    state: "TN",
    zipCode: "38474",
    phone: "(123) 456-7890",
  },
  consignee: {
    name: "Specstones Import INC",
    address: "L500 Burke Avenue Suite B",
    city: "San Francisco",
    state: "CA",
    zipCode: "94124",
    phone: "(555) 111-2312",
  },
  descriptionOfArticles: [
    "182570-0 Tile; Clay; Ceramic; or Earthenware; CL 60",
    "Actual Class: 60 Rate Class:60",
    "California Compliance surcharge",
    "Fuel Surcharge",
    "Pieces must be verified at TI me of delivery",
    "Do not breakdown pallet",
    "Do not double stack",
  ],
  amount: {
    subtotal: 16452,
    discountAmount: 536,
    other: 34,
    totalDriverCollectDue: 24,
    total: 17046,
  },
  appointmentStart: getBusinessDayStart(),
  appointmentEnd: getBusinessDayStart().add(1, "hour"),
  dueServiceDate: getBusinessDayStart().add(2, "hours"),
  reasonComment: "No problems",
  statusStartDateInUtc: dayjs("2025-12-15T08:00:00"),
};

const mockPickups = mockDispatchStops.default
  .map((stop) => stop.pickups as Shipment[])
  .flat();
const mockPickupDetails = mockPickups.reduce(
  (acc, mockPickup) => {
    acc[mockPickup.id] = {
      id: mockPickup.id,
      proNumber: mockPickup.proNumber,
      statusName: mockPickup.statusName,
      pallets: mockPickup.pallets,
      pieces: mockPickup.pieces,
      weight: mockPickup.weight,
      handlingUnits: mockPickup.handlingUnits ?? 0,
      type: "PICKUP",
      originSic: { code: "ONT", zipCode: "98472" },
      destinationSic: { code: "DAL", zipCode: "75201" },
      miscellaneous: mockDispatchMiscellaneous,
      shipmentTags: mockPickup.shipmentTags.map((tag) => ({
        ...tag,
        position: tag.position ?? 0,
      })),
      addedAccessorials: mockAddedAccessorials,
      deliveredMilesAway: null,
      ...defaultData,
    };
    return acc;
  },
  {} as Record<string, ShipmentDetails>
);

const mockDeliveries = mockDispatchStops.default
  .map((stop) => stop.deliveries as Shipment[])
  .flat();
const mockDeliveryDetails = mockDeliveries.reduce(
  (acc, mockPickup) => {
    acc[mockPickup.id] = {
      id: mockPickup.id,
      proNumber: mockPickup.proNumber,
      statusName: mockPickup.statusName,
      pallets: mockPickup.pallets,
      pieces: mockPickup.pieces,
      weight: mockPickup.weight,
      handlingUnits: mockPickup.handlingUnits ?? 0,
      type: "DELIVERY",
      originSic: { code: "ONT", zipCode: "98472" },
      destinationSic: { code: "DAL", zipCode: "75201" },
      miscellaneous: [],
      shipmentTags: mockPickup.shipmentTags.map((tag) => ({
        ...tag,
        position: tag.position ?? 0,
      })),
      addedAccessorials: mockAddedAccessorials,
      deliveredMilesAway: mockPickup.statusName === "DLVR" ? 0.3 : null,
      ...defaultData,
    };
    return acc;
  },
  {} as Record<string, ShipmentDetails>
);

export const mockShipmentDetails = {
  ...mockPickupDetails,
  ...mockDeliveryDetails,
} as Record<string, ShipmentDetails>;

export const defaultShipmentNotes = [
  {
    id: 1,
    note: "This is a unique test note",
    createdBy: "John Doe",
    createdAt: dayjs("2024-01-07T11:00:00"),
  },
  {
    id: 2,
    note: "Note for testing purposes",
    createdBy: "John Doe",
    createdAt: dayjs("2024-01-08T11:00:00"),
  },
  {
    id: 3,
    note: "Delivery is ok",
    createdBy: "John Doe",
    createdAt: dayjs("2024-01-09T11:00:00"),
  },
  {
    id: 4,
    note: "Pickup note",
    createdBy: "John Doe",
    createdAt: dayjs("2024-01-10T11:00:00"),
  },
  {
    id: 5,
    note: "As soon as possible",
    createdBy: "John Doe",
    createdAt: dayjs("2024-01-11T11:00:00"),
  },
] as ShipmentNotes[];

export const mockShipmentHistory = [
  {
    id: 1,
    sicId: 1,
    statusId: 1,
    statusName: "DLVR",
    sicName: "Ontario",
    state: "CA",
    zipCode: "98472",
    carrierName: "DYLT",
    arrivedAt: dayjs("2024-01-07T10:30:00"),
    departedAt: dayjs("2024-01-07T11:00:00"),
    statusEndDateInUtc: dayjs("2024-01-07T11:00:00"),
    statusStartDateInUtc: dayjs("2024-01-07T11:00:00"),
    createdBy: "John Doe",
    tripNumber: 374857,
    bookingNumber: "53445",
    routeName: "FLT-1",
    trailerNumber: "123456",
  },
  {
    id: 4,
    sicId: 1,
    statusId: 1,
    statusName: "LDDK",
    sicName: "Ontario",
    state: "CA",
    zipCode: "98472",
    carrierName: "DYLT",
    bookingNumber: "53445",
    tripNumber: 374857,
    statusEndDateInUtc: dayjs("2024-01-07T11:00:00"),
    statusStartDateInUtc: dayjs("2024-01-07T11:00:00"),
    createdBy: "John Doe",
    routeName: "FLT-1",
    trailerNumber: "123456",
  },
  {
    id: 2,
    sicId: 1,
    statusId: 1,
    statusName: "ATMP",
    sicName: "Ontario",
    state: "CA",
    zipCode: "98472",
    carrierName: "DYLT",
    bookingNumber: "53445",
    tripNumber: 374857,
    statusEndDateInUtc: dayjs("2024-01-07T11:00:00"),
    statusStartDateInUtc: dayjs("2024-01-07T11:00:00"),
    createdBy: "John Doe",
    routeName: "FLT-1",
    trailerNumber: "123456",
  },
  {
    id: 3,
    sicId: 1,
    statusId: 1,
    statusName: "PCKD",
    sicName: "Ontario",
    state: "CA",
    zipCode: "98472",
    carrierName: "DYLT",
    bookingNumber: "53445",
    tripNumber: 374857,
    arrivedAt: dayjs("2024-01-07T09:00:00"),
    departedAt: dayjs("2024-01-07T09:45:00"),
    statusEndDateInUtc: dayjs("2024-01-07T11:00:00"),
    statusStartDateInUtc: dayjs("2024-01-07T11:00:00"),
    createdBy: "John Doe",
    routeName: "FLT-1",
    trailerNumber: "123456",
  },
  {
    id: 5,
    sicId: 1,
    statusId: 1,
    statusName: "BDNE",
    sicName: "Ontario",
    state: "CA",
    zipCode: "98472",
    carrierName: "DYLT",
    bookingNumber: "53445",
    tripNumber: 374857,
    statusEndDateInUtc: dayjs("2024-01-07T11:00:00"),
    statusStartDateInUtc: dayjs("2024-01-07T11:00:00"),
    createdBy: "John Doe",
    routeName: "FLT-1",
    trailerNumber: "123456",
  },
] as ShipmentHistory[];

export const mockShipmentDocuments = [
  {
    id: 1,
    name: "Delivery Receipt",
    url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
    createdAt: dayjs("2024-07-07T11:00:00"),
  },
  {
    id: 2,
    name: "Bill of lading",
    url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
    createdAt: dayjs("2024-07-02T11:00:00"),
  },
  {
    id: 3,
    name: "Invoice",
    url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
    createdAt: dayjs("2025-06-30T11:00:00"),
  },
] as ShipmentDocument[];

export const mockShipmentInsights = [
  {
    id: 1,
    proNumber: "123456789",
    driver: "John Doe",
    route: "Route 1",
    consignee: "Specstones Import INC",
    address: "L500 Burke Avenue Suite B, San Francisco, CA 94124",
    city: "San Francisco",
    zip: "94124",
    pallets: 10,
    pieces: 100,
    weight: 2000,
    shipmentTags: tagNameToShipmentTagMap(["UC"]),
    status: "DLVR",
    statusStartTime: getBusinessDayStart(),
    issue: "Returned delivery",
    hasAccessorialsAdded: false,
  },
  {
    id: 2,
    proNumber: "987654321",
    driver: "Jane Smith",
    route: "Route 2",
    consignee: "Acme Corp",
    address: "123 Main St, Los Angeles, CA 90001",
    city: "Los Angeles",
    zip: "90001",
    pallets: 5,
    pieces: 50,
    weight: 1000,
    shipmentTags: tagNameToShipmentTagMap(["CC", "UC"]),
    status: "RTRN",
    statusStartTime: getBusinessDayStart().add(1, "hour"),
    issue: "Delivery exception",
    hasAccessorialsAdded: true,
  },
] as ShipmentInsights[];
